import { FC, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  createStyles,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { InfoOutlined as InfoOutlinedIcon } from "@material-ui/icons";
import { Alert, AlertTitle } from "@material-ui/lab";
import moment from "moment";

import { CustomDialog, DownloadButton } from "components";
import { TROLLEY_PAYMENT_PORTAL_URL } from "constants/index";
import { BuyerDashboardItem } from "containers/BuyerDashboard/assetAdministrationsPostProcessor";
import { RELATED_ADJUSTMENTS_ISSUED_COPY } from "containers/BuyerDashboard/constants";
import { generateAssetDistributionPDF } from "containers/PDFStatementGenerator";
import axios, { makeSimpleGetRequest } from "lib/axios";
import { fileDownloadErrorMessage } from "lib/axios/constants";
import {
  formatAmount,
  formatCurrencyAmount,
  formatHumanDate,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { getAssetDistributionTaskDownloadURL } from "services/assetDistributionTasks";
import { useManyAssetAssetAdministrationDistributions } from "services/assets";
import { getAssetAdministrationDistributionsToDisplay } from "services/assets/assetsPostProcessor";
import { getFileNameFromContentDisposition } from "services/documents";
import theme from "theme";

import ListingSection from "./ListingSection";

const tableBorderColor = theme.custom.palette.gray.dark;

const useStyles = makeStyles(() =>
  createStyles({
    customDialogTitle: {
      backgroundColor: theme.custom.palette.gray.light,
    },
    link: {
      color: theme.custom.palette.blue.main,
      fontSize: "inherit",
      "&:hover": {
        color: theme.custom.palette.blue.main,
        textDecoration: "underline",
      },
    },
    tableContainer: {
      overflowX: "unset",
    },
    table: {
      "& thead th": {
        borderBottom: `1px solid ${tableBorderColor}`,
      },
      "& tbody td": {
        borderBottom: `1px solid ${tableBorderColor}`,
      },
      "& tbody tr td:first-child": {
        borderLeft: `1px solid ${tableBorderColor}`,
      },
      "& tbody tr td:last-child": {
        borderRight: `1px solid ${tableBorderColor}`,
      },
      "& .distributionColumn": {
        maxWidth: 230,
        width: 230,
        wordBreak: "break-word",
      },
    },
    distributionContainer: {
      padding: `${theme.spacing(0)}px ${theme.spacing(2)}px ${theme.spacing(
        3,
      )}px ${theme.spacing(2)}px`,
    },
    title: {
      fontSize: "2rem",
    },
    subtitle: {
      fontSize: "1.2rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    smallText: {
      fontSize: "0.8rem",
      color: theme.custom.palette.gray.dark,
    },
    tableBody: {
      backgroundColor: theme.palette.common.white,
    },
    card: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
    },
    cardContent: {
      "& strong": {
        fontWeight: `600`,
      },
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    cardHeader: {
      fontSize: "1rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    warning: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: "100%",
    },
    alertInfo: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing(1),
      textAlign: "left",
    },
  }),
);

interface AssetAdministrationModalProps {
  onClose: () => void;
  buyerDashboardItem: BuyerDashboardItem;
}

const AssetAdministrationModal: FC<AssetAdministrationModalProps> = ({
  buyerDashboardItem,
  onClose,
}) => {
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();

  const assetAssetAdministrationDistributionQueries = useManyAssetAssetAdministrationDistributions(
    buyerDashboardItem.assetIds,
  );
  const isAssetAssetAdministrationDistributionQueriesLoading = assetAssetAdministrationDistributionQueries.some(
    query => query.isLoading,
  );
  const assetAdministrationDistributions = !isAssetAssetAdministrationDistributionQueriesLoading
    ? assetAssetAdministrationDistributionQueries.flatMap(query => query.data)
    : [];
  const assetAdministrationDistributionsWithPaymentStatus = assetAdministrationDistributions.filter(
    assetAssetAdministrationDistribution =>
      assetAssetAdministrationDistribution.payment_status,
  );
  const assetAdministrationDistributionsToDisplay = getAssetAdministrationDistributionsToDisplay(
    assetAdministrationDistributions,
  );

  const [downloadErrorMessage, setDownloadErrorMessage] = useState("");
  const handleAssetDistributionCSVDownload = async (
    assetDistributionTaskId: number,
  ) => {
    try {
      setDownloadErrorMessage("");
      const response = await axios.get(
        getAssetDistributionTaskDownloadURL(assetDistributionTaskId, true),
      );
      const fileName = getFileNameFromContentDisposition(
        response.headers["content-disposition"],
      );
      saveAs(new Blob([response.data]), fileName);
    } catch (e) {
      setDownloadErrorMessage(fileDownloadErrorMessage);
    }
  };

  const handleAssetDistributionPdfDownload = async (
    assetDistributionTaskId: number,
  ) => {
    try {
      setDownloadErrorMessage("");
      const assetDistributionJSON = await makeSimpleGetRequest(
        getAssetDistributionTaskDownloadURL(assetDistributionTaskId, false),
      );
      const { fileName, pdfFile } = generateAssetDistributionPDF(
        assetDistributionJSON || null,
        buyerDashboardItem.purchasedTerm,
        true,
      );
      pdfFile.save(fileName);
    } catch (e) {
      setDownloadErrorMessage(fileDownloadErrorMessage);
    }
  };

  return (
    <CustomDialog onClose={onClose}>
      <Box className={classes.distributionContainer}>
        <Typography variant="h1" className={classes.title}>
          {buyerDashboardItem.dealTitle ?? "Deal Details"}
        </Typography>
        <Box my={3} display="flex" justifyContent="space-between" gridGap={8}>
          {buyerDashboardItem.previousHubspotDealId ? (
            buyerDashboardItem.dealUrl ? (
              <Link
                href={buyerDashboardItem.dealUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                className={classes.link}
              >
                Deal&nbsp;#
                {buyerDashboardItem.previousHubspotDealId}
              </Link>
            ) : (
              <>Deal&nbsp;#{buyerDashboardItem.previousHubspotDealId}</>
            )
          ) : (
            <Box />
          )}
          <Typography className={classes.smallText}>
            Last updated on {formatHumanDate(buyerDashboardItem.lastUpdate)}
          </Typography>
        </Box>
        <ListingSection
          activeRelistRequestId={buyerDashboardItem.activeRelistRequestId}
          listingIds={buyerDashboardItem.listingIds}
          resaleInfoId={buyerDashboardItem.resaleInfoId}
          isIncompletePartialResale={buyerDashboardItem.incompletePartialResale}
        />
        <Box>
          <Typography className={classes.subtitle} gutterBottom>
            Payments
          </Typography>
          <Alert className={classes.alertInfo} severity="info">
            <Typography>{RELATED_ADJUSTMENTS_ISSUED_COPY}</Typography>
          </Alert>
          {!!assetAdministrationDistributionsWithPaymentStatus.length && (
            <Alert severity="warning" className={classes.warning}>
              <AlertTitle>Payment Status Update</AlertTitle>
              {assetAdministrationDistributionsWithPaymentStatus.map(
                assetAdministrationDistribution =>
                  assetAdministrationDistribution.payment_status ===
                  "Trolley recipient is inactive" ? (
                    <Typography
                      variant="body1"
                      key={
                        assetAdministrationDistribution.payment_status_updated_at
                      }
                    >
                      {formatHumanDate(
                        assetAdministrationDistribution.payment_status_updated_at,
                      )}
                      &nbsp;-&nbsp;
                      <>
                        Please complete your account setup in the{" "}
                        <Link
                          className={classes.link}
                          href={TROLLEY_PAYMENT_PORTAL_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Payment Portal
                        </Link>{" "}
                        to receive payment.
                      </>
                    </Typography>
                  ) : (
                    <Typography
                      variant="body1"
                      key={
                        assetAdministrationDistribution.payment_status_updated_at
                      }
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {formatHumanDate(
                        assetAdministrationDistribution.payment_status_updated_at,
                      )}
                      &nbsp;-&nbsp;
                      {assetAdministrationDistribution.payment_status}
                    </Typography>
                  ),
              )}
            </Alert>
          )}
          {!!downloadErrorMessage && (
            <Alert severity="error" className={classes.warning}>
              <AlertTitle>Download Error</AlertTitle>
              <Typography variant="body1">{downloadErrorMessage}</Typography>
            </Alert>
          )}
          {isAssetAssetAdministrationDistributionQueriesLoading && (
            <CircularProgress />
          )}
          {!isAssetAssetAdministrationDistributionQueriesLoading && (
            <>
              {assetAdministrationDistributionsToDisplay.length ? (
                <>
                  {isSmDown ? (
                    <Card className={classes.card}>
                      <CardContent className={classes.cardContent}>
                        {assetAdministrationDistributionsToDisplay.map(
                          (assetAdministrationDistribution, i) => {
                            const isLastRow =
                              i ===
                              assetAdministrationDistributionsToDisplay.length -
                                1;
                            const royaltiesEarned = parseNumber(
                              assetAdministrationDistribution
                                .active_asset_distribution_task.net_payable,
                            );
                            const royaltiesReceived = parseNumber(
                              assetAdministrationDistribution
                                .active_asset_distribution_task
                                .related_net_payable,
                            );
                            const unrelatedAdjustments =
                              royaltiesEarned - royaltiesReceived;
                            return (
                              <Box
                                mb={isLastRow ? 0 : 2}
                                key={assetAdministrationDistribution.id}
                              >
                                <Box width="100%" fontWeight="bold" mb={1}>
                                  {
                                    assetAdministrationDistribution.distribution
                                      .display_name
                                  }
                                </Box>
                                <Box width="100%">
                                  <Typography>
                                    <strong>Amount:</strong>{" "}
                                    {formatAmount(royaltiesEarned)}
                                  </Typography>
                                </Box>
                                <Box width="100%">
                                  <Typography>
                                    <strong>Unrelated Adjustments:</strong>{" "}
                                    {formatAmount(unrelatedAdjustments)}
                                  </Typography>
                                </Box>
                                <Box width="100%">
                                  <Typography>
                                    <strong>Royalty Received:</strong>{" "}
                                    {formatAmount(royaltiesReceived)}
                                  </Typography>
                                </Box>
                                <Box
                                  width="100%"
                                  mb={1}
                                  display="flex"
                                  alignItems="center"
                                >
                                  <Typography>
                                    <strong>Paid:&nbsp;</strong>
                                  </Typography>
                                  {assetAdministrationDistribution.paid ? (
                                    <CheckCircleIcon
                                      style={{
                                        fill: theme.custom.palette.green.main,
                                      }}
                                      fontSize="small"
                                    />
                                  ) : (
                                    <CancelIcon
                                      style={{
                                        fill: theme.custom.palette.red.main,
                                      }}
                                      fontSize="small"
                                    />
                                  )}
                                </Box>
                                <Box
                                  display="flex"
                                  width="100%"
                                  justifyContent="space-around"
                                  mb={isLastRow ? 0 : 1}
                                >
                                  <DownloadButton
                                    kind="pdf"
                                    handleDownload={() =>
                                      handleAssetDistributionPdfDownload(
                                        assetAdministrationDistribution
                                          .active_asset_distribution_task.id,
                                      )
                                    }
                                  />
                                  <DownloadButton
                                    kind="csv"
                                    handleDownload={() =>
                                      handleAssetDistributionCSVDownload(
                                        assetAdministrationDistribution
                                          .active_asset_distribution_task.id,
                                      )
                                    }
                                  />
                                </Box>
                                {!isLastRow && <hr />}
                              </Box>
                            );
                          },
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <TableContainer className={classes.tableContainer}>
                      <Table className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell className="distributionColumn">
                              Distribution
                            </TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">
                              Unrelated Adjustments{" "}
                              <Tooltip title="Adjustments that are not related to this asset's royalty earnings">
                                <InfoOutlinedIcon fontSize="small" />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              Royalty Received
                            </TableCell>
                            <TableCell align="right">Paid</TableCell>
                            <TableCell align="right">Downloads</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody className={classes.tableBody}>
                          {assetAdministrationDistributionsToDisplay.map(
                            (assetAdministrationDistribution, index) => {
                              const royaltiesEarned = parseNumber(
                                assetAdministrationDistribution
                                  .active_asset_distribution_task.net_payable,
                              );
                              const royaltiesReceived = parseNumber(
                                assetAdministrationDistribution
                                  .active_asset_distribution_task
                                  .related_net_payable,
                              );
                              const unrelatedAdjustments =
                                royaltiesEarned - royaltiesReceived;
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    align="left"
                                    className="distributionColumn"
                                  >
                                    {
                                      assetAdministrationDistribution
                                        .distribution.display_name
                                    }
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography>
                                      {formatAmount(royaltiesEarned)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography>
                                      {formatAmount(unrelatedAdjustments)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography>
                                      {formatAmount(royaltiesReceived)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography>
                                      {assetAdministrationDistribution.paid ? (
                                        <CheckCircleIcon
                                          fontSize="small"
                                          style={{
                                            fill:
                                              theme.custom.palette.green.main,
                                          }}
                                        />
                                      ) : (
                                        <CancelIcon
                                          fontSize="small"
                                          style={{
                                            fill: theme.custom.palette.red.main,
                                          }}
                                        />
                                      )}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Box
                                      mr={-2}
                                      minWidth="96px"
                                      display="flex"
                                      justifyContent="flex-end"
                                    >
                                      <DownloadButton
                                        kind="pdf"
                                        handleDownload={() =>
                                          handleAssetDistributionPdfDownload(
                                            assetAdministrationDistribution
                                              .active_asset_distribution_task
                                              .id,
                                          )
                                        }
                                      />
                                      <DownloadButton
                                        kind="csv"
                                        handleDownload={() =>
                                          handleAssetDistributionCSVDownload(
                                            assetAdministrationDistribution
                                              .active_asset_distribution_task
                                              .id,
                                          )
                                        }
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            },
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              ) : !parseNumber(buyerDashboardItem.priorPaymentAmount) ? (
                <Box mt={1}>
                  <Typography>
                    No payments processed. Statements will be available here
                    once payments are processed.
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              {!!parseNumber(buyerDashboardItem.priorPaymentAmount) && (
                <Box mt={1}>
                  <Typography variant="body1">
                    <strong>Note:</strong> Statements for the{" "}
                    {formatCurrencyAmount(
                      buyerDashboardItem.currency,
                      buyerDashboardItem.priorPaymentAmount,
                    )}{" "}
                    paid prior to 2023 are not available.{" "}
                    {moment().isBefore(moment("2024-07-12")) ? (
                      <>
                        You have until{" "}
                        {moment().isBefore(moment("2024-07-01"))
                          ? "6/30/2024"
                          : "7/11/2024"}{" "}
                        to download all historical statements from your{" "}
                        <Link
                          className={classes.link}
                          href="https://paymenthub.exactuals.com/royaltyexchange/login"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          PaymentHub
                        </Link>{" "}
                        account.
                      </>
                    ) : (
                      <>
                        Please contact{" "}
                        <Link
                          className={classes.link}
                          href="mailto:payments@royaltyexchange.com"
                        >
                          payments@royaltyexchange.com
                        </Link>{" "}
                        if you need copies of older statements.
                      </>
                    )}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <div
        style={{
          width: 600,
          height: 400,
          overflow: "hidden",
          position: "absolute",
          top: -400,
          left: -600,
        }}
      >
        <canvas id="pdf-report-canvas" width="600" height="400"></canvas>
      </div>
    </CustomDialog>
  );
};

export default AssetAdministrationModal;
